import { DataService } from "@/services/data";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Page de redirection pour les liens depuis le LMS.
 * - Si contact trouvé et accessible → redirige vers /contacts/{id}/informations
 * - Si contact non accessible → affiche un message d'erreur
 * - Si contact non trouvé → redirige vers /contacts avec l'email dans la barre de recherche globale
 * - Si erreur réseau/serveur → affiche un message d'erreur
 */
export default function ContactLMSLinkPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const redirectToContact = async () => {
            if (!email) {
                console.log("[LMS Link] Pas d'email fourni, redirection vers /contacts");
                navigate("/contacts");
                return;
            }

            try {
                const response = await DataService.client
                    .url(`/salesforce/contacts/lms-link/`)
                    .query({ email })
                    .get()
                    .json<{ contact_id?: string; error?: string }>();

                if (response.contact_id) {
                    navigate(`/contacts/${response.contact_id}/informations`);
                } else if (response.error === "forbidden") {
                    setError("Vous n'avez pas accès à ce contact.");
                } else {
                    navigate("/contacts");
                }
            } catch (err: any) {                
                if (err.status === 403) {
                    setError("Vous n'avez pas les permissions pour accéder aux contacts.");
                } else if (err.status === 401) {
                    setError("Vous devez être connecté au CRM pour accéder à cette page.");
                } else if (err.status >= 500) {
                    setError("Erreur serveur. Veuillez réessayer plus tard.");
                } else {
                    // Afficher l'erreur au lieu de rediriger silencieusement
                    setError(`Une erreur est survenue lors de la recherche du contact. ${err.message || ''}`);
                }
            }
        };

        redirectToContact();
    }, [email, navigate]);

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center max-w-md">
                    <div className="mb-4 text-red-500">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès impossible</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    {email && (
                        <p className="text-sm text-gray-500 mb-4">
                            Email recherché : <span className="font-mono">{email}</span>
                        </p>
                    )}
                    <button
                        onClick={() => navigate("/contacts")}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Retour aux contacts
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center h-screen">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-gray-600">Recherche du contact...</p>
                {email && (
                    <p className="mt-2 text-sm text-gray-500">
                        Email : <span className="font-mono">{email}</span>
                    </p>
                )}
            </div>
        </div>
    );
}
